import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { useEventLogging } from '../../models/Logging';
import { isValidEmail } from '../../models/UiUtils';
import { Button as LegacyButton } from '../../web/components/Button';
import { XIcon } from '../../web/components/svgs/icons/XIcon';
import { Input } from '../../web/components-v2/Input';
import { navigation } from '../../web/styles/colors';
import { useErrorMessage } from '../contexts/errorMessage';
import { useMeeting } from '../contexts/meeting';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	inputId?: string;
}

export const ScheduleMeetingGuests: React.FC<IProps> = ({ className = '', inputId = 'guest-email' }) => {
	const {
		createNewGuestParticipant,
		guestParticipants,
		hasInvalidGuestEmail,
		removeGuestParticipant,
		setHasInvalidGuestEmail,
	} = useMeeting();
	const { setError } = useErrorMessage();
	const logger = useEventLogging();
	const [newGuest, setNewGuest] = useState<string>('');

	const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setHasInvalidGuestEmail(false);
		setNewGuest(e.target.value);
	};

	const addGuest = () => {
		const emailAddress = newGuest.trim();
		if (!emailAddress) {
			return;
		}
		if (!isValidEmail(emailAddress)) {
			setHasInvalidGuestEmail(true);
			return;
		}
		setNewGuest('');
		if (guestParticipants?.some(g => g.emailAddress?.toLowerCase() === emailAddress.toLowerCase())) {
			return;
		}
		createNewGuestParticipant(emailAddress).catch((err: Api.IOperationResultNoValue) => {
			setNewGuest(current => current || emailAddress);
			logger.logApiError('SchedulerCreateGuest-Error', err);
			setError(err);
		});
	};

	const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			addGuest();
		}
	};

	const onRemoveGuestClick = (guest: Api.ISchedulerContact) => () => {
		removeGuestParticipant(guest).catch((err: Api.IOperationResultNoValue) => {
			logger.logApiError('SchedulerRemoveGuest-Error', err);
			setError(err);
		});
	};

	return (
		<div className={`${css(styleSheet.container)} ${className}`}>
			<Input
				autoComplete='off'
				id={inputId}
				onBlur={addGuest}
				onChange={onInputChange}
				onKeyDown={onInputKeyDown}
				placeholder='Guest email address'
				type='email'
				value={newGuest}
			/>
			{hasInvalidGuestEmail && <p className={css(styleSheet.error)}>Enter a valid email address</p>}
			<div className={css(styleSheet.meetingGuestsContainer)}>
				{(guestParticipants || []).map((g, i) => (
					<div key={i} className={css(styleSheet.text, styleSheet.meetingGuest)}>
						<LegacyButton
							accessory={<XIcon color={navigation} height={12} width={12} />}
							kind='icon'
							label=''
							onClick={onRemoveGuestClick(g)}
						/>
						{g.emailAddress}
					</div>
				))}
			</div>
		</div>
	);
};
